import React from 'react';
import { SiNvidia, SiGooglecloud } from 'react-icons/si';
import type { IconType, IconBaseProps } from 'react-icons';

/**
 * Small brand marks and language flags used on the About page and the home
 * Experience section. Everything is sized for inline use next to mono text.
 */

export const MicrosoftLogo: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 23 23" className={className} aria-hidden="true">
    <rect x="0" y="0" width="11" height="11" fill="#F25022" />
    <rect x="12" y="0" width="11" height="11" fill="#7FBA00" />
    <rect x="0" y="12" width="11" height="11" fill="#00A4EF" />
    <rect x="12" y="12" width="11" height="11" fill="#FFB900" />
  </svg>
);

export const NvidiaLogo: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <SiNvidia className={className} style={{ color: '#76B900' }} aria-hidden="true" />
);

export const GoogleCloudLogo: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <SiGooglecloud className={className} style={{ color: '#4285F4' }} aria-hidden="true" />
);

/**
 * OpenAI mark, inlined rather than imported from react-icons/si: simple-icons
 * dropped the OpenAI glyph upstream (present through react-icons 5.6.0, gone
 * in 5.7.0), which broke production builds resolving the "^5.5.0" range fresh.
 * This is the exact path from the last version that had it, so it renders
 * identically and can no longer disappear from under us.
 */
export const OpenAiIcon: IconType = (props: IconBaseProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" role="img" {...props}>
    <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
  </svg>
);

/** Spartan helmet: natural MSU green in light mode, ink-white in dark mode. */
export const MsuLogo: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <img
    src="/WorkExperience/msu.webp"
    alt="Michigan State University"
    className={`${className} object-contain dark:[filter:brightness(0)_invert(1)]`}
  />
);

export const UsFlag: React.FC<{ className?: string }> = ({ className = 'w-5 h-auto' }) => (
  <svg viewBox="0 0 60 42" className={`${className} border border-foreground/15`} aria-hidden="true">
    <rect width="60" height="42" fill="#FFFFFF" />
    {[0, 2, 4, 6, 8, 10, 12].map((i) => (
      <rect key={i} y={(i * 42) / 13} width="60" height={42 / 13} fill="#B22234" />
    ))}
    <rect width="26" height={(42 / 13) * 7} fill="#3C3B6E" />
    {Array.from({ length: 12 }, (_, i) => (
      <circle
        key={i}
        cx={4 + (i % 4) * 6}
        cy={4 + Math.floor(i / 4) * 7.5}
        r="1.3"
        fill="#FFFFFF"
      />
    ))}
  </svg>
);

export const KuwaitFlag: React.FC<{ className?: string }> = ({ className = 'w-5 h-auto' }) => (
  <svg viewBox="0 0 60 30" className={`${className} border border-foreground/15`} aria-hidden="true">
    <rect width="60" height="10" fill="#007A3D" />
    <rect y="10" width="60" height="10" fill="#FFFFFF" />
    <rect y="20" width="60" height="10" fill="#CE1126" />
    <polygon points="0,0 15,10 15,20 0,30" fill="#000000" />
  </svg>
);

/** Certification chip: brand mark + label. */
export const CertChip: React.FC<{ logo: React.ReactNode; label: string; className?: string; style?: React.CSSProperties }> = ({
  logo,
  label,
  className = '',
  style,
}) => (
  <span
    className={`inline-flex items-center gap-2.5 px-4 py-2 border border-foreground/10 text-sm font-mono ${className}`}
    style={style}
  >
    {logo}
    {label}
  </span>
);
